<template>
  <div>
    <v-flex
      xs12
      class="text-xs-center text-sm-center text-md-center text-lg-center"
    >
      <v-btn color="pink" :class="$style.btnTextStyle" dark @click="pickFile">
        {{ lang.image_add_dialog_select_image }}
      </v-btn>

      <v-btn
        v-if="!isPanel"
        :class="$style.btnTextStyle"
        @click="onClickAddBlankButton"
      >
        {{ lang.image_add_dialog_add_blank_image }}
      </v-btn>

      <input
        type="file"
        style="display: none"
        ref="image"
        :multiple="!isPanel"
        accept="image/png,image/jpeg, image/jpg"
        @change="onFilePicked"
      />
      <v-alert
        v-for="(message, key, index) in errorMessages"
        :key="index"
        :value="true"
        type="error"
      >
        {{ message }}
      </v-alert>
    </v-flex>
    <v-flex class="text-xs-right"> </v-flex>
  </div>
</template>
<script>
import _ from "lodash";
import log from "loglevel";
import promiseLimit from "promise-limit";
import ua from "../../../lib/useragent.js";
import {
  dataUrlToBlob,
  loadImageWithOrientationCorrection
} from "../../../lib/image";
const limit = promiseLimit(6);
import blankImage from "./assets/blank.png";

const MAX_IMAGE_NUM = 200;
const MAX_IMAGE_FILE_SIZE_HUMAN = 32;
const MAX_IMAGE_FILE_SIZE = 1024 * 1024 * MAX_IMAGE_FILE_SIZE_HUMAN;
const PANEL_IMAGE_UPLOAD_WAIT_TIME_MSEC = 4000;

const MAX_WIDTH = 960;
// リサイズ時の高さの上限を大きくすることで、縦読み・横読みを切り替えた時にくずれないようにする
const MAX_HEIGHT = 20000;

export default {
  name: "vc-share-imageupload",
  data: () => ({
    title: "Image Upload",
    imageName: "",
    imageUrl: "",
    imageFile: "",
    firstFile: "",
    errorMessages: [],
    tempImageFile: "",
    tempImageDataUrl: "",
    blankImage: blankImage
  }),
  props: {
    lang: {
      type: Object,
      default: () => ({
        image_add_dialog_select_image: "画像を追加",
        image_add_dialog_upload: "アップロード",
        image_add_dialog_add_blank_image: "空白ページを追加",
        image_add_error_image_num: "追加できる画像は :num 枚までです。",
        image_add_error_image_size:
          ":filename : :max_size MB より小さい画像を選択してください",
        image_add_error_file_type:
          ":filename : jpeg もしくは png の画像を選択してください。"
      })
    },
    isPanel: {
      type: Boolean,
      default: false
    },

    // stateToProps
    draftId: {
      type: Number,
      default: null
    },
    images: {
      type: Array,
      default: function() {
        return [];
      }
    },
    pageProgressionDirection: {
      type: String,
      default: "rtl"
    },

    // gettersToProps
    getNotIncludeCoverDataImageCount: {
      type: Number,
      default: 0
    },

    // mutationsToProps
    setIsBusy: {
      type: Function,
      default: () => {}
    },
    pushImages: {
      type: Function,
      default: () => {}
    },
    setUploadingImageCount: {
      type: Function,
      default: () => {}
    },

    // actionsToProps
    postDraftImage: {
      type: Function,
      default: async () => {}
    },
    sortImages: {
      type: Function,
      default: () => {}
    },
    postDraftBlankImage: {
      type: Function,
      default: () => {}
    },
    postCoverImage: {
      type: Function,
      default: () => {}
    },
    setInitialStaticImageData: {
      type: Function,
      default: () => {}
    },
    putBlankArea: {
      type: Function,
      default: () => {}
    },
    deleteBlankArea: {
      type: Function,
      default: () => {}
    }
  },
  methods: {
    // 「画像を追加」ボタン
    pickFile() {
      this.$refs.image.click();
    },
    // 「空白ページを追加」ボタン
    async onClickAddBlankButton() {
      const vue = this;
      vue.errorMessages = [];
      if (vue.images.length >= MAX_IMAGE_NUM) {
        const message = vue.lang.image_add_error_image_num.replace(
          ":num",
          MAX_IMAGE_NUM
        );
        vue.errorMessages.push(message);
        return;
      }

      this.setIsBusy(true);
      this.$emit("start-upload");

      if (this.getNotIncludeCoverDataImageCount === 0) {
        await this.setInitialStaticImageData();
      }

      this.postDraftBlankImage()
        .then(() => {
          // props だと反映されていないため直接参照
          const isContainsBlankArea =
            vue.$store.state.uploadContents.images.filter(
              image => image.staticImageName === "blankArea"
            ).length > 0;
          if (isContainsBlankArea) {
            return this.putBlankArea();
          } else {
            return this.deleteBlankArea();
          }
        })
        .then(
          () => {
            setTimeout(() => {
              this.setIsBusy(false);
            }, 1);
          },
          () => {
            setTimeout(() => {
              this.setIsBusy(false);
            }, 1);
          }
        );
    },
    // ファイル選択時の処理
    async onFilePicked(e) {
      const startTime = performance.now(); // FIXME
      const files = e.target.files;
      const vue = this;
      vue.errorMessages = [];
      log.debug("onFilePicked files", files);

      if (
        this.getNotIncludeCoverDataImageCount + files.length >
        MAX_IMAGE_NUM
      ) {
        const message = vue.lang.image_add_error_image_num.replace(
          ":num",
          MAX_IMAGE_NUM
        );
        vue.errorMessages.push(message);
        return;
      }
      let isValid = true;
      _.each(files, file => {
        isValid = vue.isValidImage(file);
        return isValid;
      });
      if (!isValid) {
        return;
      }
      const afterValidationTime = performance.now(); // FIXME
      log.info("validation time: " + (afterValidationTime - startTime)); // FIXME

      this.setUploadingImageCount(files.length);
      this.setIsBusy(true);
      if (this.getNotIncludeCoverDataImageCount === 0) {
        await this.setInitialStaticImageData();
      }

      const tasks = [];
      for (let file of files) {
        const image = await vue.processImageOnClient(file);
        tasks.push(async () => {
          await vue.uploadImageFile(image.preImageId, image.file, image.src);
        });
      }
      const afterClientProcessTime = performance.now(); // FIXME
      log.info(
        "clientProcess time: " + (afterClientProcessTime - afterValidationTime)
      );

      if (tasks.length > 0) {
        await this.processTasks(tasks);
        const afterUploadProcessTime = performance.now(); // FIXME
        log.info(
          "uploadProcess time: " +
            (afterUploadProcessTime - afterClientProcessTime)
        );
        if (this.errorMessages.length === 0) {
          this.$emit("start-upload");
        }
      }
    },
    // アップロードされたファイルのチェック
    isValidImage(file) {
      const vue = this;
      if (file.type !== "image/jpeg" && file.type !== "image/png") {
        const message = vue.lang.image_add_error_file_type.replace(
          ":filename",
          file.name
        );
        vue.errorMessages.push(message);
        return false;
      }
      if (file.size > MAX_IMAGE_FILE_SIZE) {
        const message = vue.lang.image_add_error_image_size
          .replace(":filename", file.name)
          .replace(":max_size", MAX_IMAGE_FILE_SIZE_HUMAN);
        vue.errorMessages.push(message);
        return false;
      }
      return true;
    },
    // 画像ファイル一件に対するクライアント処理
    // リサイズ/回転がをawaitするのでasyncにする必要があるが、この処理自体は同期的に実行する
    async processImageOnClient(file) {
      const vue = this;
      log.debug("onFilePicked each", file);
      const preImageId = parseInt(_.uniqueId()) * -1;
      let imagePreviewUrl;
      // UAを参照してクライアントサイドでのリサイズ/ローテートを行う
      if (ua.getBrowser().name === "IE" || ua.getBrowser().name === "Edge") {
        // IEの場合: ローカルでは何もしない & プレビュー用ObjectURLはブランク画像にする
        log.debug("detected as PC(IE or Edge)");
        imagePreviewUrl = this.blankImage;
      } else if (JSON.stringify(ua.getDevice()) !== "{}" && !this.isPanel) {
        // モバイル（パネル以外）の場合: リサイズ＋ローテート
        log.debug("detected as mobile: " + JSON.stringify(ua.getDevice()));
        const canvas = await loadImageWithOrientationCorrection(file, {
          maxWidth: MAX_WIDTH,
          maxHeight: MAX_HEIGHT
        });
        imagePreviewUrl = canvas.toDataURL();
        file = vue.blobToFile(dataUrlToBlob(imagePreviewUrl), file);
      } else {
        // PC(IE以外)の場合: ローテート
        log.debug("detected as PC(not IE)");
        const canvas = await loadImageWithOrientationCorrection(file, {
          maxWidth: 2048,
          maxHeight: MAX_HEIGHT
        });
        imagePreviewUrl = canvas.toDataURL();
        file = vue.blobToFile(dataUrlToBlob(imagePreviewUrl), file);
      }
      // 画像をstore(state.imagesとstate.uploadingImage)に格納する
      vue.pushImages({
        imageId: preImageId,
        src: imagePreviewUrl,
        isBlank: false,
        name: file.name,
        isBody: true
      });
      return {
        preImageId: preImageId,
        src: imagePreviewUrl,
        file: file
      };
    },
    // アップロード処理の実行、完了後に空枠の生成とソートを行う
    processTasks(tasks) {
      const vue = this;
      return Promise.all(
        tasks.map(task => {
          return limit(() => task());
        })
      )
        .then(() => {
          // props だと反映されていないため直接参照
          const isContainsBlankArea =
            vue.$store.state.uploadContents.images.filter(
              image => image.staticImageName === "blankArea"
            ).length > 0;
          if (isContainsBlankArea) {
            return vue.putBlankArea();
          } else {
            return vue.deleteBlankArea();
          }
        })
        .then(() => {
          if (!this.isPanel) {
            // ソート処理の実行
            // props だと反映されていないため直接参照
            const images = vue.$store.state.uploadContents.images;
            this.sortImages({
              draftId: vue.draftId,
              afterImages: images,
              beforeImages: images
            }).then(() => {
              vue.setIsBusy(false);
            });
          } else {
            setTimeout(() => {
              vue.setIsBusy(false);
            }, PANEL_IMAGE_UPLOAD_WAIT_TIME_MSEC);
          }
        });
    },
    // 画像をS3へアップロードする
    async uploadImageFile(preImageId, imageFile, imagePreviewUrl) {
      const vue = this;
      await vue
        .postDraftImage({
          preImageId: preImageId,
          isBlank: false,
          name: imageFile.name,
          url: imagePreviewUrl,
          file: imageFile,
          isBody: true
        })
        .then(async () => {
          if (this.firstFile === "") {
            this.firstFile = imageFile;
            if (this.isPanel) {
              await vue.setFrontCoverImageForPanel(this.firstFile);
            }
          }
        });
    },
    // 受け取ったBlob(Canvas#toBlobの変換結果)と元ファイルの情報からFileオブジェクトを作成する
    blobToFile(blob, file) {
      return new File([blob], file.name, {
        type: file.type,
        lastModified: Date.now()
      });
    },
    // パネル投稿時にアップロード画像を表紙としてセットする
    setFrontCoverImageForPanel(file) {
      this.postCoverImage({
        file: file,
        name: file.name,
        width: file.width,
        height: file.height
      });
    }
  }
};
</script>

<style module>
.btnTextStyle {
  text-transform: none !important;
}
</style>
